import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaGraduationCap, FaHandshake, FaLightbulb, FaMicrophone, FaUniversity, FaChalkboardTeacher, FaGlobe, FaRunning } from 'react-icons/fa';
import './CommunityImpact.css';

// Import images
import GlobalCitizenForumLogo from '../images/logos/GlobalCitizenForum.png';
import UBCSenateLogo from '../images/logos/UBCSenate.jpg';
import UBCEngrLogo from '../images/logos/UBC_Engr.jpg';
import StaxFitnessLogo from '../images/logos/StaxFitness.png';
import SPIELogo from '../images/logos/SPIE.png';

interface Initiative {
  id: string;
  title: string;
  period: string;
  description: string;
  image: string;
  category: string;
  icon: JSX.Element;
  link?: string;
}

// Community Impact data
const communityInitiatives: Initiative[] = [
  {
    id: "engineering-symposium",
    title: "5th Annual Engineering Graduate Symposium",
    period: "2018-2019",
    description: "Led the organization of the 5th Annual Research Engineering Symposium which was recognized as the 'Most successful Engineering Graduate Symposium to date'. Coordinated panels discussing emerging trends in machine learning, data science, and applications solving real-world engineering challenges.",
    image: "https://picsum.photos/seed/symposium/800/450",
    category: "Leadership",
    icon: <FaUniversity />,
    link: "https://engineering.ok.ubc.ca/2019/02/19/most-successful-engineering-graduate-symposium-to-date/"
  },
  {
    id: "community-fitness",
    title: "Community Fitness Leader & Organizer",
    period: "2025-Present",
    description: "Established and lead bi-weekly community fitness programs across British Columbia. Coordinate diverse outdoor activities including park workouts, group hikes, beach volleyball, and paddleboarding adventures. Created inclusive, family-friendly environments that foster community engagement and social connection through physical activity.",
    image: "https://picsum.photos/seed/fitness-community/800/450",
    category: "Health & Wellness",
    icon: <FaRunning />
  },
  {
    id: "ubc-panels",
    title: "3X Panelist @ The University of British Columbia",
    period: "2018-2020",
    description: "Served as an expert panelist for career development events, discussing emerging trends in machine learning and data science. Participated in research symposiums and educational panels focused on mentoring graduate students, while engaging in discussions about diversity and inclusion in STEM fields.",
    image: "https://picsum.photos/seed/panels/800/450",
    category: "Speaking",
    icon: <FaMicrophone />
  },
  {
    id: "wgli-mentor",
    title: "Mentor at We Guide Learning Initiative",
    period: "2022-Present",
    description: "Provide guidance and mentorship to individuals across Africa, helping them achieve academic, career, and personal goals in data science, machine learning, cloud computing, and software development. Conduct workshops on advanced topics like predictive modeling and cloud-based deployment.",
    image: "https://picsum.photos/seed/mentor/800/450",
    category: "Mentorship",
    icon: <FaChalkboardTeacher />
  },
  {
    id: "egss-president",
    title: "President of Engineering Graduate Student Society",
    period: "May 2018-May 2019",
    description: "Served as the graduate student liaison between faculty and departmental staff at UBC Okanagan. Represented graduate students on the School of Engineering Graduate Studies committee and at Faculty Council meetings. Organized events and ensured a conducive environment for graduate students.",
    image: "https://picsum.photos/seed/president/800/450",
    category: "Leadership",
    icon: <FaGraduationCap />
  },
  {
    id: "binance-angel",
    title: "Binance Angel - Community Educator on Blockchain",
    period: "2020-Present",
    description: "Act as a liaison between Binance communities across continents. Create online content and guides for using the Binance platform. Represent Binance within my community with active market feedback, moderate online communities, and test beta versions of Binance resources.",
    image: "https://picsum.photos/seed/blockchain/800/450",
    category: "Education",
    icon: <FaGlobe />
  },
  {
    id: "homecoming-organizer",
    title: "Graduate Lead Member - UBCO Homecoming Committee",
    period: "March 2018-Present",
    description: "Led graduate student representation in organizing the inaugural UBCO Homecoming event, bringing together alumni, students, and community members. Coordinated activities including sports events and alumni gatherings with notable speakers like Amazing Race Canada host Jon Montgomery.",
    image: "https://picsum.photos/seed/homecoming/800/450",
    category: "Event Planning",
    icon: <FaUsers />
  }
];

// Impact areas data
const impactAreas = [
  {
    title: "Educational Leadership",
    description: "Bridging gaps between academia and industry through panels, symposiums, and mentoring initiatives.",
    icon: <FaGraduationCap />,
    color: "#e50914"
  },
  {
    title: "Community Building",
    description: "Creating inclusive spaces for collaboration, learning, and growth across diverse communities.",
    icon: <FaUsers />,
    color: "#0097e6"
  },
  {
    title: "Knowledge Sharing",
    description: "Democratizing access to cutting-edge knowledge in machine learning, data science, and blockchain.",
    icon: <FaLightbulb />,
    color: "#44bd32"
  },
  {
    title: "Global Connections",
    description: "Fostering cross-cultural collaboration and mentorship spanning Africa, Canada, and global tech communities.",
    icon: <FaHandshake />,
    color: "#f39c12"
  },
  {
    title: "Health & Wellness",
    description: "Promoting physical activity and community bonding through inclusive, accessible fitness programs across British Columbia.",
    icon: <FaRunning />,
    color: "#8e44ad"
  }
];

const affiliations = [
  {
    name: "Global Citizen Forum",
    logo: GlobalCitizenForumLogo,
    link: "https://www.globalcitizenforum.org"
  },
  {
    name: "UBC Senate",
    logo: UBCSenateLogo,
    link: "https://senate.ubc.ca"
  },
  {
    name: "School of Engineering",
    logo: UBCEngrLogo,
    link: "https://engineering.ok.ubc.ca"
  },
  {
    name: "StaxFitness",
    logo: StaxFitnessLogo,
    link: "https://staxfitnesscanada.com/"
  },
  {
    name: "SPIE",
    logo: SPIELogo,
    link: "https://spie.org"
  }
];

const CommunityImpact: React.FC = () => {
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(communityInitiatives.map(i => i.category)));
  
  const filteredInitiatives = activeCategory 
    ? communityInitiatives.filter(i => i.category === activeCategory)
    : communityInitiatives;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleInitiativeClick = (initiative: Initiative) => {
    setSelectedInitiative(initiative);
  };

  const closeModal = () => {
    setSelectedInitiative(null);
  };

  return (
    <div className="community-container">
      <motion.div 
        className="community-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <h1>Community Leadership</h1>
          <p>Building bridges, empowering minds, and creating lasting impact</p>
        </div>
      </motion.div>

      <div className="category-filter">
        <button 
          className={activeCategory === null ? 'active' : ''} 
          onClick={() => setActiveCategory(null)}
        >
          All
        </button>
        {categories.map(category => (
          <button 
            key={category}
            className={activeCategory === category ? 'active' : ''}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <motion.div 
        className="initiatives-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredInitiatives.map((initiative) => (
          <motion.div
            key={initiative.id}
            className="initiative-card"
            variants={cardVariants}
            onClick={() => handleInitiativeClick(initiative)}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.2 }}
          >
            <div className="initiative-image">
              <img src={initiative.image} alt={initiative.title} />
              <div className="initiative-overlay">
                <div className="initiative-icon">{initiative.icon}</div>
                <div className="initiative-category">{initiative.category}</div>
                <div className="initiative-period">{initiative.period}</div>
              </div>
            </div>
            <h3>{initiative.title}</h3>
          </motion.div>
        ))}
      </motion.div>

      <motion.div 
        className="impact-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <h2>Areas of Impact</h2>
        <div className="impact-grid">
          {impactAreas.map((area, index) => (
            <motion.div 
              key={index} 
              className="impact-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 * index, duration: 0.5 }}
            >
              <div className="impact-icon" style={{ backgroundColor: area.color }}>
                {area.icon}
              </div>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.section 
        className="affiliations-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="affiliations-title">Affiliations & Memberships</h2>
        <div className="affiliations-grid">
          {affiliations.map((affiliation, index) => (
            <motion.a
              key={affiliation.name}
              href={affiliation.link}
              target="_blank"
              rel="noopener noreferrer"
              className="affiliation-item"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <img 
                src={affiliation.logo} 
                alt={`${affiliation.name} logo`} 
                className="affiliation-logo"
              />
              <span className="affiliation-name">{affiliation.name}</span>
            </motion.a>
          ))}
        </div>
      </motion.section>

      {selectedInitiative && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-image">
              <img src={selectedInitiative.image} alt={selectedInitiative.title} />
              <div className="modal-badge">
                {selectedInitiative.icon}
                <span>{selectedInitiative.category}</span>
              </div>
            </div>
            <div className="modal-info">
              <h2>{selectedInitiative.title}</h2>
              <p className="modal-period">{selectedInitiative.period}</p>
              <p className="modal-description">{selectedInitiative.description}</p>
              {selectedInitiative.link && (
                <a 
                  href={selectedInitiative.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="modal-link"
                >
                  Learn more
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default CommunityImpact; 